import { useEffect, useState, useCallback, useRef } from 'react';

/**
 * 自定义Hook：计算并提供元素尺寸和缩放比例
 * 根据选择的宽高比自动调整显示区域
 */
export default function useComputedSize() {
  // 维护一个参考尺寸的常量对象
  const aspectRatios = useRef({
    landscape: { w: 1920, h: 1080 }, // 横屏 16:9
    square: { w: 1080, h: 1080 }, // 正方形 1:1
    portrait: { w: 1080, h: 1920 }, // 竖屏 9:16
  }).current;
  const [dimensions, setDimensions] = useState({ width: 1080, height: 1920 });
  // 缩放比例
  const [scaleRatio, setScaleRatio] = useState(1);

  // 设计与实际尺寸
  const [bgDesignWH, setbgDesignWH] = useState({
    x: 0,
    y: 0,
    w: aspectRatios.landscape.w,
    h: aspectRatios.landscape.h,
    realx: 0,
    realy: 0,
    realw: aspectRatios.landscape.w,
    realh: aspectRatios.landscape.h,
    radius: '10px',
  });

  // 当前选择的宽高比（0: 16:9, 1: 1:1, 2: 9:16）
  const [selectedAspectRatio, setSelectedAspectRatio] = useState(0);

  /**
   * 更新元素尺寸和比例
   */
  const updateElementSize = useCallback(() => {
    const div = document.getElementById('rightvideoId');
    if (!div) return;

    const containerWidth = div.offsetWidth;
    const containerHeight = div.offsetHeight;

    let targetWidth, targetHeight, targetRatio;
    let baseWidth, baseHeight;

    // 根据选择的宽高比设置基准尺寸
    switch (selectedAspectRatio) {
      // 横屏 16:9
      case 0:
        baseWidth = aspectRatios.landscape.w;
        baseHeight = aspectRatios.landscape.h;
        targetRatio = 16 / 9;
        break;
      // 正方形 1:1
      case 1:
        baseWidth = aspectRatios.square.w;
        baseHeight = aspectRatios.square.h;
        targetRatio = 1;
        break;
      // 竖屏 9:16
      case 2:
        baseWidth = aspectRatios.portrait.w;
        baseHeight = aspectRatios.portrait.h;
        targetRatio = 9 / 16;
        break;
      default:
        // 未自测 初步想象的方法
        baseWidth = dimensions.width;
        baseHeight = dimensions.height;
        targetRatio = dimensions.width / dimensions.height;
        console.log(targetRatio, 'targetRatio');
    }

    // 计算实际尺寸，确保适应容器且保持宽高比
    const containerRatio = containerWidth / containerHeight;

    if (containerRatio > targetRatio) {
      // 容器较宽，以高度为基准
      targetHeight = containerHeight;
      targetWidth = targetHeight * targetRatio;
    } else {
      // 容器较高，以宽度为基准
      targetWidth = containerWidth;
      targetHeight = targetWidth / targetRatio;
    }

    // 计算缩放比例
    const newScaleRatio = targetWidth / baseWidth;

    // 更新状态
    setScaleRatio(newScaleRatio);
    setbgDesignWH({
      x: 0,
      y: 0,
      w: baseWidth,
      h: baseHeight,
      realx: (containerWidth - targetWidth) / 2, // 居中显示
      realy: (containerHeight - targetHeight) / 2, // 居中显示
      realw: targetWidth,
      realh: targetHeight,
      radius: '10px',
    });
  }, [selectedAspectRatio, aspectRatios, dimensions]);

  /**
   * 当宽高比选择改变时，重新计算尺寸
   */
  useEffect(() => {
    updateElementSize();

    // 监听窗口大小变化，动态调整尺寸
    window.addEventListener('resize', updateElementSize);
    return () => {
      window.removeEventListener('resize', updateElementSize);
    };
  }, [selectedAspectRatio, updateElementSize]);

  return {
    scaleRatio,
    bgDesignWH,
    dimensions,
    setDimensions,
    selectedAspectRatio,
    setSelectedAspectRatio,
    updateElementSize,
  };
}
